import { useMutation, useQueryClient } from "@tanstack/react-query"
import { extractApiErrorMessage } from "@velizhanin-dev/api-errors"
import { Archive, ArchiveRestore, ExternalLink, KeyRound, Workflow } from "lucide-react"
import { type ReactNode, useState } from "react"
import { useNavigate } from "react-router-dom"
import { toast } from "sonner"
import {
	BotStatusBadge,
	archiveBot,
	botBuilderKeys,
	type CustomBot,
	replaceBotToken,
	restoreBot,
	updateBot,
} from "@/entities/bot-builder"
import { useSessionRole } from "@/entities/session"
import { ScenarioPicker } from "@/features/choose-bot-scenario"
import { cn } from "@/shared/lib/utils"
import { Button } from "@/shared/ui/button"
import { useConfirm } from "@/shared/ui/confirm-provider"
import { Field } from "@/shared/ui/form-field"
import { Input } from "@/shared/ui/input"
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogHeader,
	DialogTitle,
} from "@/shared/ui/dialog"
import { Disclosure } from "@/shared/ui/disclosure"
import { Switch } from "@/shared/ui/switch"

const formatDateTime = (value: string) =>
	new Date(value).toLocaleString("ru-RU", { dateStyle: "short", timeStyle: "short" })

/** Блок настроек: одна карточка на смысловую группу, без вложенных карточек. */
function Panel({
	title,
	description,
	className,
	children,
}: {
	title?: string
	description?: string
	className?: string
	children: ReactNode
}) {
	return (
		<section className={cn("rounded-2xl border border-border bg-card p-5", className)}>
			{title ? (
				<header className="mb-4">
					<h3 className="text-sm font-medium">{title}</h3>
					{description ? <p className="mt-0.5 text-xs text-muted-foreground">{description}</p> : null}
				</header>
			) : null}
			{children}
		</section>
	)
}

export function BotSettings({ bot }: { bot: CustomBot }) {
	const navigate = useNavigate()
	const queryClient = useQueryClient()
	const { confirm } = useConfirm()
	const { isAdmin } = useSessionRole()
	const archived = bot.archivedAt !== null
	const [name, setName] = useState(bot.name)
	const [token, setToken] = useState("")
	const [choosing, setChoosing] = useState(false)

	const applyBot = async (next: CustomBot) => {
		queryClient.setQueryData(botBuilderKeys.bot(next.id), next)
		await queryClient.invalidateQueries({ queryKey: botBuilderKeys.bots })
		await queryClient.invalidateQueries({ queryKey: botBuilderKeys.scenarios })
	}

	const restore = useMutation({
		mutationFn: () => restoreBot(bot.id),
		onSuccess: async (next) => {
			await applyBot(next)
			toast.success("Бот возвращён из архива. Он выключен, включите его, когда будете готовы")
		},
		onError: (error) => toast.error(extractApiErrorMessage(error, "Не удалось вернуть бота")),
	})

	const update = useMutation({
		mutationFn: (patch: Parameters<typeof updateBot>[1]) => updateBot(bot.id, patch),
		onSuccess: applyBot,
		onError: (error) => toast.error(extractApiErrorMessage(error, "Не удалось сохранить бота")),
	})

	const replaceToken = useMutation({
		mutationFn: () => replaceBotToken(bot.id, token.trim()),
		onSuccess: async (next) => {
			await applyBot(next)
			setToken("")
			toast.success("Токен заменён, бот перезапустится")
		},
		onError: (error) => toast.error(extractApiErrorMessage(error, "Не удалось заменить токен")),
	})

	const scenarioUnpublished = !!bot.scenarioId && !bot.scenario?.latestVersion

	return (
		<div className="space-y-4">
			{archived ? (
				<Panel className="flex flex-wrap items-center justify-between gap-3 border-amber-400/40 bg-amber-400/5">
					<div>
						<p className="font-medium">Бот в архиве</p>
						<p className="text-sm text-muted-foreground">
							Он не отвечает пользователям. Платежи, аудитория и история сохранены.
						</p>
					</div>
					{isAdmin ? (
						<Button variant="outline" disabled={restore.isPending} onClick={() => restore.mutate()}>
							<ArchiveRestore />
							Вернуть из архива
						</Button>
					) : null}
				</Panel>
			) : null}

			<fieldset disabled={archived} className="min-w-0 space-y-4 disabled:opacity-60">
				<Panel className="space-y-4">
					<div className="flex flex-wrap items-center gap-2">
						<BotStatusBadge status={bot.status} />
						<a
							href={`https://t.me/${bot.username}`}
							target="_blank"
							rel="noreferrer"
							className="inline-flex items-center gap-1 text-sm text-primary hover:underline"
						>
							@{bot.username}
							<ExternalLink className="size-3.5" />
						</a>
						<span className="text-xs text-muted-foreground">токен …{bot.tokenLast4}</span>
					</div>
					{bot.status === "ERROR" && bot.lastError ? (
						<p className="rounded-xl border border-destructive/40 bg-destructive/10 p-3 text-sm text-red-300">
							{bot.lastError}
							{bot.lastErrorAt ? (
								<span className="mt-1 block text-xs text-muted-foreground">
									{formatDateTime(bot.lastErrorAt)}
								</span>
							) : null}
						</p>
					) : null}
					<label
						className="flex cursor-pointer items-center justify-between gap-4 border-t border-border pt-4"
						htmlFor="bot-enabled"
					>
						<span>
							<span className="block text-sm font-medium">Бот включён</span>
							<span className="block text-xs text-muted-foreground">
								Выключенный бот не отвечает пользователям
							</span>
						</span>
						<Switch
							id="bot-enabled"
							checked={bot.enabled}
							disabled={update.isPending}
							onCheckedChange={(enabled) => update.mutate({ enabled })}
						/>
					</label>
					<label
						className="flex cursor-pointer items-center justify-between gap-4 border-t border-border pt-4"
						htmlFor="bot-helper"
					>
						<span>
							<span className="block text-sm font-medium">Вспомогательный бот</span>
							<span className="block text-xs text-muted-foreground">
								Только ищет Telegram ID: уходит во вкладку «Вспомогательные», поиск не останавливается
							</span>
						</span>
						<Switch
							id="bot-helper"
							checked={bot.isHelper}
							disabled={update.isPending}
							onCheckedChange={(isHelper) => update.mutate({ isHelper })}
						/>
					</label>
				</Panel>

				<Panel title="Сценарий" description="По нему бот отвечает людям: воронки, меню, группы.">
					{bot.scenario ? (
						<div className="flex flex-wrap items-center gap-3">
							<span className="grid size-10 shrink-0 place-items-center rounded-xl bg-secondary">
								<Workflow className="size-4" />
							</span>
							<div className="min-w-0 flex-1">
								<p className="truncate text-sm font-medium">{bot.scenario.name}</p>
								<p className="text-xs text-muted-foreground">
									{scenarioUnpublished
										? "Не опубликован: бот начнёт отвечать после публикации"
										: `Работает версия ${bot.scenario.latestVersion?.number}`}
								</p>
							</div>
							<div className="flex gap-2">
								<Button
									variant="secondary"
									size="sm"
									onClick={() => navigate(`/admin/bots/${bot.id}/constructor/funnels`)}
								>
									Открыть воронки
								</Button>
								<Button variant="ghost" size="sm" onClick={() => setChoosing(true)}>
									Сменить
								</Button>
							</div>
						</div>
					) : (
						<ScenarioPicker bot={bot} />
					)}
				</Panel>

				<Panel title="Название в админке" description="Видно только в панели, людям в Telegram не показывается.">
					<form
						className="flex gap-2"
						onSubmit={(event) => {
							event.preventDefault()
							if (name.trim() && name.trim() !== bot.name) update.mutate({ name: name.trim() })
						}}
					>
						<Input
							id="bot-name"
							placeholder="Например, Бот клуба"
							aria-label="Название в админке"
							maxLength={64}
							value={name}
							onChange={(event) => setName(event.target.value)}
						/>
						<Button
							type="submit"
							variant="outline"
							disabled={!name.trim() || name.trim() === bot.name || update.isPending}
						>
							Сохранить
						</Button>
					</form>
				</Panel>
			</fieldset>

			{isAdmin && !archived ? (
				<Disclosure variant="card" title="Технические настройки">
					<div className="space-y-5">
						<form
							onSubmit={(event) => {
								event.preventDefault()
								if (token.trim()) replaceToken.mutate()
							}}
						>
							<Field
								label="Заменить токен"
								htmlFor="bot-new-token"
								hint="Если токен отозвали в @BotFather. Подойдёт только токен этого же бота."
							>
								<div className="flex gap-2">
									<Input
										id="bot-new-token"
										type="password"
										autoComplete="off"
										spellCheck={false}
										placeholder="Новый токен"
										value={token}
										onChange={(event) => setToken(event.target.value)}
									/>
									<Button
										type="submit"
										variant="outline"
										disabled={!token.trim() || replaceToken.isPending}
									>
										<KeyRound />
										Заменить
									</Button>
								</div>
							</Field>
						</form>

						<div className="flex flex-wrap items-center justify-between gap-3 border-t border-border pt-5">
							<div>
								<p className="text-sm font-medium">Убрать в архив</p>
								<p className="text-xs text-muted-foreground">
									Бот перестанет отвечать. Историю и аудиторию можно будет вернуть.
								</p>
							</div>
							<Button
								variant="outline"
								className="text-red-400 hover:text-red-300"
								onClick={() =>
									confirm({
										title: `Убрать @${bot.username} в архив?`,
										message:
											"Бот перестанет отвечать, запланированные рассылки снимутся. Платежи, аудитория и история сохранятся, бота можно будет вернуть.",
										confirmText: "В архив",
										onConfirm: async () => {
											try {
												await applyBot(await archiveBot(bot.id))
											} catch (error) {
												toast.error(extractApiErrorMessage(error, "Не удалось убрать бота в архив"))
												return
											}
											await queryClient.invalidateQueries({ queryKey: botBuilderKeys.archivedBots })
											navigate("/admin/bots", { replace: true })
										},
									})
								}
							>
								<Archive />
								В архив
							</Button>
						</div>
					</div>
				</Disclosure>
			) : null}
			<Dialog open={choosing} onOpenChange={setChoosing}>
				<DialogContent className="sm:max-w-lg">
					<DialogHeader>
						<DialogTitle>Сменить сценарий</DialogTitle>
						<DialogDescription>
							Бот сразу начнёт работать по новому сценарию. Старый сохранится, его можно вернуть.
						</DialogDescription>
					</DialogHeader>
					<ScenarioPicker bot={bot} onDone={() => setChoosing(false)} />
				</DialogContent>
			</Dialog>
		</div>
	)
}
